import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, BookOpen, CheckCircle2, Loader2, Sparkles } from "lucide-react";

const COVER = "/guia-cover.jpg";

export function GuideDownloadCTA({ theme = "dark" }) {
  const [open, setOpen] = useState(false);
  const dark = theme === "dark";

  return (
    <>
      <div
        data-testid="guide-cta-card"
        className="relative overflow-hidden rounded-3xl flex flex-col sm:flex-row items-stretch"
        style={{ background: dark ? "#5A1E2C" : "#fff", border: dark ? "none" : "1px solid rgba(90,30,44,0.12)", boxShadow: dark ? "none" : "0 8px 30px rgba(90,30,44,0.08)" }}
      >
        <div className="sm:w-52 shrink-0 flex items-center justify-center p-6 sm:p-8" style={{ background: dark ? "rgba(0,0,0,0.15)" : "#F7F4EF" }}>
          <img src={COVER} alt="Portada de la guía de cata de vinos Vinonotas" className="w-32 sm:w-full max-w-[150px] rounded-lg shadow-xl" />
        </div>
        <div className="flex-1 p-7 sm:p-10">
          <p className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: "#C8A96A" }}>Folleto gratuito · PDF</p>
          <h3 className="font-serif text-2xl sm:text-3xl leading-tight mb-3" style={{ color: dark ? "#fff" : "#5A1E2C" }}>
            Guía gratuita para aprender a catar vinos
          </h3>
          <p className="text-[15px] leading-relaxed mb-6 max-w-xl" style={{ color: dark ? "rgba(255,255,255,0.72)" : "#555" }}>
            Descárgala gratis: las tres fases de la cata (visual, olfativa y gustativa) explicadas con lenguaje sencillo,
            consejos prácticos y un glosario con todos los términos del catador.
          </p>
          <button
            data-testid="guide-download-open-btn"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            style={{ background: "#C8A96A", color: "#5A1E2C" }}
          >
            <Download className="w-4 h-4" /> Descargar guía gratis
          </button>
        </div>
      </div>
      <LeadDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function LeadDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const triggerDownload = async (url) => {
    // url looks like /api/leads/{id}/guia.pdf ; api baseURL already has /api
    const path = url.replace(/^\/api/, "");
    const res = await api.get(path, { responseType: "blob" });
    const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = "Guia-Vinonotas-aprende-a-catar.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!name || !email || !phone) {
      toast.error("Completa todos los campos");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Introduce un correo electrónico válido");
      return;
    }
    if (phone.replace(/\D/g, "").length < 6) {
      toast.error("Introduce un número de teléfono válido");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/leads", { name, email, phone });
      setDownloadUrl(data.download_url);
      await triggerDownload(data.download_url);
      setDone(true);
      toast.success("¡Listo! Tu guía se está descargando");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo generar la guía");
    } finally {
      setLoading(false);
    }
  };

  const redownload = async () => {
    if (!downloadUrl) return;
    try {
      await triggerDownload(downloadUrl);
    } catch {
      toast.error("No se pudo descargar. Inténtalo de nuevo.");
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setDownloadUrl(null); setForm({ name: "", email: "", phone: "" }); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="guide-lead-dialog" className="sm:max-w-md">
        {done ? (
          <div className="text-center py-6" data-testid="guide-lead-success">
            <CheckCircle2 className="w-14 h-14 mx-auto text-green-600 mb-4" />
            <h3 className="font-serif text-2xl text-borgona-700 mb-2">¡Tu guía está en camino!</h3>
            <p className="text-sm text-grises-500 mb-6">La descarga debería haber comenzado. Si no, pulsa el botón para reintentar.</p>
            <button
              data-testid="guide-redownload-btn"
              onClick={redownload}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white bg-borgona-700 hover:bg-borgona-900 transition-colors"
            >
              <Download className="w-4 h-4" /> Descargar de nuevo
            </button>
            <div className="mt-6 pt-6 border-t border-grises-200">
              <p className="text-sm text-grises-600 mb-3">Lleva tus catas al siguiente nivel:</p>
              <a
                href="/register"
                data-testid="guide-signup-link"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5"
                style={{ background: "#C8A96A", color: "#5A1E2C" }}
              >
                <Sparkles className="w-4 h-4" /> Crea tu cuenta gratuita
              </a>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="w-11 h-11 rounded-xl bg-borgona-50 flex items-center justify-center mb-1">
                <BookOpen className="w-5 h-5 text-borgona-700" />
              </div>
              <DialogTitle className="font-serif text-2xl text-borgona-700">Descarga tu guía gratuita</DialogTitle>
              <DialogDescription className="text-grises-500">
                Déjanos tus datos y te enviamos el folleto en PDF al instante.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} noValidate className="space-y-4 mt-2">
              <div>
                <Label className="text-grises-600 text-sm">Nombre</Label>
                <Input data-testid="lead-name-input" value={form.name} onChange={set("name")} placeholder="Tu nombre" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-grises-600 text-sm">Correo electrónico</Label>
                <Input data-testid="lead-email-input" type="email" value={form.email} onChange={set("email")} placeholder="tucorreo@ejemplo.com" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-grises-600 text-sm">Celular / WhatsApp</Label>
                <Input data-testid="lead-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+34 600 000 000" className="mt-1.5 h-11" />
              </div>
              <button
                data-testid="lead-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white bg-borgona-700 hover:bg-borgona-900 transition-colors disabled:opacity-60"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando…</> : <><Download className="w-4 h-4" /> Descargar guía gratis</>}
              </button>
              <p className="text-[11px] text-grises-400 text-center leading-relaxed">
                Al descargar aceptas recibir comunicaciones de Vinonotas. Puedes darte de baja cuando quieras.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default GuideDownloadCTA;
